import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { saveAs } from 'file-saver';
import { round } from 'lodash';
import * as moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';

import { FileType } from '@shared/components/files-input/files-input.component';
import { FilesSubmitComponent } from '@shared/components/files-submit/files-submit.component';
import { ImportShelfInventoryBarcode } from '@shared/models/ImportShelfInventoryBarcode';
import { LayoutActionLoadError } from '@shared/store/actions/layout.action';
import { b64toBlob } from '@shared/utils/b64toBlob-util';
import { ExcelValidateUtil } from '@shared/utils/excel-validate-util';

import { environment as env, environment } from '../../../../../environments/environment';
import { SearchBarcodeModalComponent } from '../../../../shared/components/search-barcode-modal/search-barcode-modal.component';
import { SearchProductModalComponent } from '../../../../shared/components/search-product-modal/search-product-modal.component';
import { UploadDocumentButtonComponent } from '../../../../shared/components/upload-document-button/upload-document-button.component';
import { ProductTypeEnum } from '../../../../shared/enum/product-type.enum';
import { NewRequestStatusEnum } from '../../../../shared/enum/request-status.enum';
import {
  NewRequestTypeEnum,
  RequestPageModesEnum,
  RequestProductErrorEnum
} from '../../../../shared/enum/request-step.enum';
import { ShelfPages } from '../../../../shared/enum/td-store-page.enum';
import { Classes } from '../../../../shared/gql/classes.gql';
import { ProductGradings } from '../../../../shared/gql/product-grading.gql';
import { AlertModalComponent } from '../../../../shared/layouts';
import { SimpleInfoListModalComponent } from '../../../../shared/layouts/modals/simple-info-list-modal/simple-info-list-modal.component';
import { UiPaginationComponent } from '../../../../shared/layouts/ui-pagination/ui-pagination.component';
import { BarcodeResponse } from '../../../../shared/models/barcode.model';
import { ButtonType, ImportExportButton, ImportTypeEnum } from '../../../../shared/models/import-export-button.model';
import { fixtureTypeList as fixtureTypeListImport } from '../../../../shared/models/list-value/list-key-value.model';
import { MetricAction, MetricModule, MetricType, Metrics } from '../../../../shared/models/metrics';
import { ShelfInventoryRequestViewResponse } from '../../../../shared/models/shelf-inventory-request.model';
import {
  DeleteShelfItems,
  FirstLotImportItem,
  InventoryImportItem,
  ListOfChanges,
  MerchandiseItem,
  ShelfRequestViewResponse
} from '../../../../shared/models/shelf-request.model';
import { AuthGuardService } from '../../../../shared/services';
import { ShelfInventoryRequestService } from '../../../../shared/services/shelf-inventory-request.service';
import { ShelfRequestService } from '../../../../shared/services/shelf-request.service';
import { AppStates } from '../../../../shared/store/state/app.states';
import { getSelectShelf, getSelectShelfPage } from '../../../../shared/utils/get-select-by-page-util';

@Component({
  selector: 'app-shelf-inventory',
  templateUrl: './shelf-inventory.component.html',
  styleUrls: ['./shelf-inventory.component.scss']
})
export class ShelfInventoryComponent extends OnDestroyMixin implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() parentForm: UntypedFormGroup;
  @Input() submitted: boolean;
  @Input() mode: RequestPageModesEnum;
  @Input() page: ShelfPages;
  @ViewChild('searchProductModal') searchProductModal: SearchProductModalComponent;
  @ViewChild('searchBarcodeModal') searchBarcodeModal: SearchBarcodeModalComponent;
  @ViewChild('importShelf') importShelf: UploadDocumentButtonComponent;
  @ViewChild('importItem') importItem: UploadDocumentButtonComponent;
  @ViewChild('paging') paging: UiPaginationComponent;

  public productType: ProductTypeEnum = ProductTypeEnum.INVENTORY;
  public headerRow: string[];
  public localStore: Observable<any>;
  public shelfRequestView$: Observable<ShelfRequestViewResponse>;
  public shelfInventoryRequestView$: Observable<ShelfInventoryRequestViewResponse>;
  public requestNo: string;
  public type: NewRequestTypeEnum;
  public status: NewRequestStatusEnum;
  public productGradings: Array<ProductGradings>;
  public classes: Array<Classes>;
  public shelfCode: string;
  public listOfChange: ListOfChanges[] = [];
  public deleteMerchandiseItem: DeleteShelfItems[];
  public importBarcodeUrl: string;
  public importFirstLot = '';
  public importHeaders;
  public fixtureTypeList = fixtureTypeListImport;
  public currentPage = 1;
  public pageSize = 20;
  public buttons: Array<ImportExportButton>;
  public metricsRequest: Metrics;
  private errorFile: any;
  private isValidate: boolean;
  private filesSubmitModal: BsModalRef;
  private dataFilesStoreImport: ImportShelfInventoryBarcode;

  constructor(
    private readonly fb: UntypedFormBuilder,
    protected readonly store: Store<AppStates>,
    private readonly shelfRequestService: ShelfRequestService,
    private readonly shelfInventoryRequestService: ShelfInventoryRequestService,
    private readonly logger: NGXLogger,
    protected readonly modalService: BsModalService,
    private readonly translate: TranslateService,
    private readonly authGuardService: AuthGuardService
  ) {
    super();
  }

  ngOnInit() {
    this.headerRow = [
      'Product Name',
      'Barcode',
      'Unit',
      'Bay No./Level',
      'Location ID',
      'Facing Quantity',
      'Stacking Quantity',
      'Depth Quantity',
      'Max Refill Quantity',
      'Fixture Type',
      'Action',
      'Change Status'
    ];

    this.initData();
    this.initState();
    this.getButton();
  }

  ngAfterViewInit(): void {
    if (this.page === ShelfPages.SHELF_REQUEST_INVENTORY || this.page === ShelfPages.SHELF_INVENTORY_EDIT) {
      if (this.page === ShelfPages.SHELF_REQUEST_INVENTORY) {
        this.importBarcodeUrl = `${env.serverUrl}${env.services.shelfInventoryRequest.url}${env.services.shelfInventoryRequest.importBarcodes}`;
      } else if (this.page === ShelfPages.SHELF_INVENTORY_EDIT) {
        this.importBarcodeUrl = `${env.serverUrl}${env.services.shelfInventoryRequest.url}${env.services.shelfInventoryRequest.importBarcodes}`;
      }
      this.importHeaders = env.services.shelfInventoryRequest.headers;
      this.metricsRequest = this.getMetrics(
        `${env.services.shelfInventoryRequest.url}${env.services.shelfInventoryRequest.importBarcodes}`
      );
    } else {
      this.importBarcodeUrl = `${env.serverUrl}${env.services.shelfRequest.url}${env.services.shelfRequest.importBarcodes}`;
      this.importHeaders = env.services.shelfRequest.headers;
      this.metricsRequest = this.getMetrics(
        `${env.services.shelfRequest.url}${env.services.shelfRequest.importBarcodes}`
      );
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  getMetrics(url): Metrics {
    return {
      metrics: [
        {
          metricType: MetricType.COUNTER,
          module: MetricModule.SHELF_INVENTORY,
          path: url
        },
        {
          metricType: MetricType.RESPONSE_TIME,
          module: MetricModule.SHELF_INVENTORY,
          route: url,
          action: MetricAction.IMPORT
        }
      ]
    };
  }

  getButton() {
    this.buttons = [
      {
        type: ButtonType.EXPORT,
        name: 'Export Item',
        hidden: !this.isViewMode || !this.isShowExportItemButton
      },
      {
        type: ButtonType.IMPORT,
        name: 'Import Shelf',
        by: ImportTypeEnum.SHELF,
        hidden: this.isViewMode || !this.isShowImportShelfButton
      },
      {
        type: ButtonType.IMPORT,
        name: 'Import Item',
        by: ImportTypeEnum.ITEM,
        hidden: this.isViewMode || !this.isShowImportItemButton
      }
    ];
  }

  initState() {
    this.localStore = this.store.pipe(untilComponentDestroyed(this));

    const formArray = this.fb.array([], [this.locationIdDuplicateValidator()]);

    this.parentForm.addControl('inventoryItem', formArray);

    if (this.mode !== RequestPageModesEnum.REQUEST_CREATE) {
      if (this.page === ShelfPages.SHELF_REQUEST_INVENTORY || this.page === ShelfPages.SHELF_INVENTORY_EDIT) {
        this.shelfInventoryRequestView$ = this.localStore.pipe(select(getSelectShelfPage(this.page)));
        this.setInventoryRequestItemsValue();
      } else {
        this.shelfRequestView$ = this.localStore.pipe(select(getSelectShelf(this.page)));
        this.setInventoryItemsValue();
      }
    }
  }

  initData() {
    this.shelfInventoryRequestService.getProductGrading().subscribe(result => {
      if (result.data) {
        this.productGradings = result.data.productGradings;
      }
    });

    this.shelfInventoryRequestService.getClasses().subscribe(result => {
      if (result.data) {
        this.classes = result.data.classes;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const mode = changes['mode'] && changes['mode'].currentValue;
    if (mode && mode === RequestPageModesEnum.REQUEST_EDIT) {
      this.toggleEditForm();
    }
  }

  get form(): UntypedFormArray {
    return this.parentForm.get('inventoryItem') as UntypedFormArray;
  }

  get formControls(): AbstractControl[] {
    return (this.parentForm.get('inventoryItem') as UntypedFormArray).controls;
  }

  setInventoryRequestItemsValue() {
    this.shelfInventoryRequestView$
      .pipe(
        untilComponentDestroyed(this),
        filter(value => Boolean(value))
      )
      .subscribe(value => {
        const params =
          this.page === ShelfPages.SHELF_REQUEST_INVENTORY
            ? `?shelfRequestId=${value.id}`
            : `?shelfNo=${value.shelfNo}`;
        this.type = value.type;
        this.status = value.status;
        this.requestNo = value.requestNo;
        this.shelfCode = value.shelfCode;
        this.deleteMerchandiseItem = value.deleteItems || [];
        this.importFirstLot = `${env.serverUrl}${env.services.shelfInventoryRequest.url}${env.services.shelfInventoryRequest.importFirstLot}${params}`;
        this.metricsRequest = this.getMetrics(
          `${env.services.shelfInventoryRequest.url}${env.services.shelfInventoryRequest.importFirstLot}`
        );

        this.getButton();

        const merchandiseItemLength = value.items && value.items.length;

        if (merchandiseItemLength && !this.form.controls.length) {
          this.setInventoryItems(value.items);
          this.form.setValidators([this.locationIdDuplicateValidator()]);
        }

        if (this.mode === RequestPageModesEnum.REQUEST_EDIT) {
          this.toggleEditForm();
        }
      });
  }

  setInventoryItems(items: MerchandiseItem[]) {
    this.parentForm.setControl('inventoryItem', this.fb.array(items.map(item => this.createForm(item, false))));

    this.listOfChange.push(
      ...items
        .filter(item => item.changeStatus)
        .map(item => ({
          barcode: item.barcode,
          bayNo: item.bayNo,
          bayLevel: item.bayLevel,
          locationId: item.locationId,
          changeStatus: item.changeStatus,
          changes: item.changes ? item.changes.map(x => x.field) : []
        }))
    );
  }

  setInventoryItemsValue() {
    this.shelfRequestView$
      .pipe(
        untilComponentDestroyed(this),
        filter(value => Boolean(value))
      )
      .subscribe(value => {
        const params =
          this.page === ShelfPages.SHELF_REQUEST ? `?shelfRequestId=${value.id}` : `?shelfNo=${value.no}`;
        this.type = value.type;
        this.status = value.status;
        this.requestNo = value.requestNo;
        this.shelfCode = value.shelfCode;
        this.deleteMerchandiseItem = value.deleteMerchandiseItem || [];
        this.importFirstLot = `${env.serverUrl}${env.services.shelfRequest.url}${env.services.shelfRequest.importFirstLot}${params}`;
        this.metricsRequest = this.getMetrics(
          `${env.services.shelfRequest.url}${env.services.shelfRequest.importFirstLot}`
        );
        this.getButton();

        const merchandiseItemLength = value.merchandiseItem && value.merchandiseItem.length;

        if (merchandiseItemLength && !this.form.controls.length) {
          this.setInventoryItems(value.merchandiseItem);

          this.form.setValidators([this.locationIdDuplicateValidator()]);
        }

        if (this.mode === RequestPageModesEnum.REQUEST_EDIT) {
          this.toggleEditForm();
        }
      });
  }

  createForm(item: MerchandiseItem, isCalculateMinQty: boolean, errorMessage?: RequestProductErrorEnum) {
    const isDisabled = this.mode === RequestPageModesEnum.REQUEST_VIEW;
    const isProductError = !!errorMessage;
    const { bayNo, bayLevel } = this.getBayValues(item);

    const formItem: UntypedFormGroup = this.fb.group({
      id: item.id || null,
      barcode: item.barcode,
      classCode: item.classCode,
      articleNo: item.articleNo,
      productName: item.productName,
      productLocation: item.productLocation,
      unit: item.unit,
      unitFactor: item.unitFactor,
      baseUnit: item.baseUnit,
      productGrading: item.productGrading,
      plg: item.plg,
      fixtureType: [{ value: item.fixtureType || null, disabled: isDisabled }, [Validators.required]],
      bayNo: [
        { value: isProductError ? null : bayNo ? parseInt(bayNo) : null, disabled: isDisabled },
        [Validators.required, this.setValidator(RequestProductErrorEnum.NOT_ALLOW_ZERO)]
      ],
      bayLevel: [
        { value: isProductError ? null : String(bayLevel ?? ''), disabled: isDisabled },
        [Validators.required, this.setValidator(RequestProductErrorEnum.NOT_ALLOW_ZERO)]
      ],
      facingQty: [
        { value: isProductError ? null : item.facingQty, disabled: isDisabled },
        [Validators.required, this.setValidator(RequestProductErrorEnum.NOT_ALLOW_ZERO)]
      ],
      stackingQty: [
        { value: isProductError ? null : item.stackingQty, disabled: isDisabled },
        [Validators.required, this.setValidator(RequestProductErrorEnum.NOT_ALLOW_ZERO)]
      ],
      depthQty: [
        { value: isProductError ? null : item.depthQty, disabled: isDisabled },
        [Validators.required, this.setValidator(RequestProductErrorEnum.NOT_ALLOW_ZERO)]
      ],
      minFirstLotQty: [
        { value: item.minFirstLotQty, disabled: true },
        [Validators.required, this.setValidator(RequestProductErrorEnum.NOT_ALLOW_ZERO)]
      ],
      minBeautyQty: [
        { value: item.minBeautyQty, disabled: true },
        [Validators.required, this.setValidator(RequestProductErrorEnum.NOT_ALLOW_ZERO)]
      ],
      maxRefillQuantity: [
        { value: item.maxRefillQuantity, disabled: isDisabled },
        [Validators.required, this.setValidator(RequestProductErrorEnum.NOT_ALLOW_ZERO)]
      ],
      locationId: [
        { value: item.locationId, disabled: isDisabled },
        [Validators.required, this.setValidator(RequestProductErrorEnum.NOT_ALLOW_ZERO)]
      ]
    });

    if (isCalculateMinQty) {
      this.calculateGradingQty(formItem);
    }

    if (errorMessage) {
      this.validateForm(formItem, errorMessage);
    }

    return formItem;
  }

  toggleEditForm() {
    this.mode = RequestPageModesEnum.REQUEST_EDIT;
    this.getButton();
    let editableFields = [];

    if (this.status === NewRequestStatusEnum.AWAITING_FIRST_LOT || this.status === NewRequestStatusEnum.ACTIVE) {
      editableFields = ['minFirstLotQty', 'minBeautyQty'];
    } else {
      editableFields = [
        'facingQty',
        'stackingQty',
        'depthQty',
        'bayNo',
        'bayLevel',
        'maxRefillQuantity',
        'locationId',
        'fixtureType'
      ];
    }

    if (!this.form) {
      const formArray = this.fb.array([], [this.locationIdDuplicateValidator()]);
      this.parentForm.addControl('inventoryItem', formArray);
    }

    this.form.disable({ emitEvent: false, onlySelf: true });

    this.formControls.forEach(item => {
      editableFields.forEach(field => {
        item.get(field).enable({ emitEvent: false, onlySelf: true });
      });
    });

    this.form.updateValueAndValidity({ emitEvent: true });
  }

  addNewItem() {
    this.searchProductModal.openSelectProductModal();
  }

  addNewBarcode() {
    this.searchBarcodeModal.selectBarcodeModal.show();
  }

  onAddItem(response: BarcodeResponse[]) {
    response.forEach(item => {
      this.form.push(
        this.createForm(MerchandiseItem.mappingBarcodeResponseToMerchandiseItem(item), false, item.errorMessage)
      );
      this.form.markAsTouched();
    });
  }

  onAddBarcodeItem(response: BarcodeResponse[]) {
    response.forEach(item => {
      this.form.push(
        this.createForm(MerchandiseItem.mappingBarcodeResponseToMerchandiseItem(item), true, item.errorMessage)
      );
    });

    this.form.markAsTouched();
  }

  onLoadItemDetail(value: InventoryImportItem[]) {
    // Reset form
    this.form.clear();

    const form = new UntypedFormArray([], [this.locationIdDuplicateValidator()]);
    value.forEach(item => {
      form.push(this.createForm(this.prepareInventoryItemFromImport(item), true, item.product.errorMessage));
    });

    this.parentForm.setControl('inventoryItem', form);
    this.form.markAsTouched();
  }

  onLoadFirstLotItemDetail(value: FirstLotImportItem[]) {
    value.forEach(item => {
      const formUpdate = this.formControls.find(x => x.get('barcode').value === item.barcode);

      if (formUpdate) {
        formUpdate.get('minFirstLotQty').patchValue(item.minFirstLotQty);
        formUpdate.get('minBeautyQty').patchValue(item.minBeautyQty);

        formUpdate.updateValueAndValidity();
      }
    });
  }

  prepareInventoryItemFromImport(response: InventoryImportItem) {
    return {
      ...MerchandiseItem.mappingBarcodeResponseToMerchandiseItem(response.product),
      barcode: response.barcode,
      bay: response.bay,
      facingQty: response.facingQty,
      stackingQty: response.stackingQty,
      depthQty: response.depthQty,
      maxRefillQuantity: response.maxRefillQuantity,
      minFirstLotQty: null,
      minBeautyQty: null,
      locationId: response.locationId,
      fixtureType: response.fixtureType
    };
  }

  validatorFormControls(field: string, i: number) {
    const index = i + (this.currentPage - 1) * this.pageSize;
    return this.formControls[index].get(field).errors;
  }

  deleteItem(i: number) {
    const index = i + (this.currentPage - 1) * this.pageSize;
    this.form.removeAt(index);
    this.form.markAsTouched();
  }

  onExport() {
    const params = this.getParamExport();

    if (this.page === ShelfPages.SHELF_REQUEST_INVENTORY || this.page === ShelfPages.SHELF_INVENTORY_EDIT) {
      this.shelfInventoryRequestService.exportInventoryItem(params).subscribe({
        next: response => {
          const blob = new Blob([response]);
          saveAs(blob, this.getGeneratedFileName(Object.values(params)[0]));
        },
        error: error => {
          if (error.error.code === '00004') {
            this.alertErrorModal(error.error.message);
          } else {
            this.store.dispatch(new LayoutActionLoadError(error));
          }
        }
      });
    } else {
      this.shelfRequestService.exportInventoryItem(params).subscribe({
        next: response => {
          const blob = new Blob([response]);
          saveAs(blob, this.getGeneratedFileName(Object.values(params)[0]));
        },
        error: error => {
          if (error.error.code === '00004') {
            this.alertErrorModal(error.error.message);
          } else {
            this.store.dispatch(new LayoutActionLoadError(error));
          }
        }
      });
    }
  }

  onClickImport(importType: ImportTypeEnum) {
    if (importType === ImportTypeEnum.SHELF) {
      this.importShelf.checkExistingUpload(true, () => {
        this.filesSubmitModal = this.modalService.show(FilesSubmitComponent, {
          backdrop: 'static',
          keyboard: false,
          initialState: {
            title: 'Import',
            templateName: 'Shelf Inventory Template',
            fileTitle: 'Shelf Inventory',
            btnSubmitLabel: 'OK',
            isEnableFileInput: true,
            confirmSubmitText: '',
            loading: false
          }
        });

        this.filesSubmitModal.content.downloadTemplate.pipe(untilComponentDestroyed(this)).subscribe(() => {
          this.downloadTemplate();
        });

        this.filesSubmitModal.content.upload
          .pipe(
            switchMap((file: File) =>
              ExcelValidateUtil.validate(file, {
                format: [FileType.XLSX]
              }).pipe(
                tap(validateMessage => {
                  this.filesSubmitModal.content.loading = false;
                  if (validateMessage?.errorMessage) {
                    this.filesSubmitModal.content.errorMessage = validateMessage?.errorMessage;
                  } else {
                    this.upload(file);
                    this.filesSubmitModal.content.errorMessage = null;
                  }
                })
              )
            ),
            untilComponentDestroyed(this)
          )
          .subscribe();

        this.filesSubmitModal.content.submit.pipe(untilComponentDestroyed(this)).subscribe(() => {
          this.filesSubmitModal.content.isUploadSuccess = true;
          this.filesSubmitModal.content.loading = false;
          this.filesSubmitModal.content.cssDisabled = true;
          this.onSubmitImport();
        });
        this.filesSubmitModal.content.downloadErrorExcel.pipe(untilComponentDestroyed(this)).subscribe(() => {
          this.downloadErrorExcel();
        });
        this.filesSubmitModal.content.error.pipe(untilComponentDestroyed(this)).subscribe(() => {
          this.filesSubmitModal.content.loading = false;
          this.filesSubmitModal.content.cssDisabled = true;
        });
      });
    } else if (importType === ImportTypeEnum.ITEM) {
      this.importItem.checkExistingUpload();
    }
  }

  downloadTemplate() {
    this.shelfInventoryRequestService.downloadShelfInventoryRequestTemplate().subscribe({
      next: response => {
        this.saveFile(response);
      },
      error: error => {
        this.modalService.show(AlertModalComponent, {
          initialState: {
            title: 'Failed',
            message: error.error.message
          }
        });
      }
    });
  }

  getParamExport() {
    let result;
    if (this.page === ShelfPages.SHELF_REQUEST || this.page === ShelfPages.SHELF_REQUEST_INVENTORY) {
      result = { requestNo: this.requestNo };
    } else {
      result = { shelfCode: this.shelfCode };
    }
    return result;
  }

  getGeneratedFileName(requestNo) {
    const date = new Date();
    const formattedDate = moment(date).format('DDMMYYYYHHmm');

    return `${requestNo}_${formattedDate}.xlsx`;
  }

  locationIdDuplicateValidator(): ValidatorFn {
    return (fa: UntypedFormArray) => {
      const rows = fa.getRawValue();
      const seen = new Set<string>();
      const duplicates: number[] = [];

      rows.forEach((item, i) => {
        const { bayNo, bayLevel, locationId } = item;

        if (!bayNo || !bayLevel || !locationId) return;

        const key = `${bayNo}_${bayLevel}_${locationId}`;

        if (seen.has(key)) {
          duplicates.push(i);
        } else {
          seen.add(key);
        }
      });

      fa.controls.forEach(ctrl => {
        const lc = ctrl.get('locationId');

        if (ctrl.hasError('duplicatedLocationId')) {
          const { duplicatedLocationId, ...rest } = ctrl.errors || {};
          ctrl.setErrors(Object.keys(rest).length ? rest : null);
        }

        if (lc?.hasError('duplicatedLocationId')) {
          const { duplicatedLocationId, ...rest } = lc.errors || {};
          lc.setErrors(Object.keys(rest).length ? rest : null);
        }
      });

      duplicates.forEach(i => {
        const ctrl = fa.controls[i];
        const lc = ctrl.get('locationId');

        ctrl.setErrors({ ...(ctrl.errors || {}), duplicatedLocationId: true });
        lc?.setErrors({ ...(lc.errors || {}), duplicatedLocationId: true });
      });

      return duplicates.length > 0 ? { duplicatedLocationId: true } : null;
    };
  }

  calculateGradingQty(formItem: AbstractControl) {
    const facingQty = formItem.get('facingQty').value;
    const stackingQty = formItem.get('stackingQty').value;
    const depthQty = formItem.get('depthQty').value;
    const classCode = formItem.get('classCode').value;

    if (
      this.isNoData(facingQty) ||
      this.isNoData(stackingQty) ||
      this.isNoData(depthQty) ||
      this.isNoData(classCode)
    ) {
      this.logger.debug('facingQty, stackingQty, depthQty or classCode is null. Cannot calculate.');

      formItem.get('minFirstLotQty').patchValue(null);
      formItem.get('minBeautyQty').patchValue(null);

      formItem.updateValueAndValidity();

      return;
    }

    const productGrading = formItem.get('productGrading').value;

    const firstLotFromGrading = this.productGradings.find(item => item.code === productGrading)?.firstLotPct || 0;
    const beautyFromGrading = this.productGradings.find(item => item.code === productGrading)?.beautyPct || 0;
    const firstLotFromClass = this.classes.find(item => item.code === classCode)?.firstLotPct || 0;
    const beautyFromClass = this.classes.find(item => item.code === classCode)?.beautyPct || 0;

    // Calculate First Lot Qty
    const firstLotDepthQty = round((firstLotFromGrading / 100) * depthQty);
    const maxFirstLotQty = firstLotDepthQty >= firstLotFromClass ? firstLotDepthQty : firstLotFromClass;
    const comparedMinFirstLot = maxFirstLotQty <= depthQty ? maxFirstLotQty : depthQty;
    const minFirstLotQty = facingQty * stackingQty * comparedMinFirstLot;

    formItem.get('minFirstLotQty').patchValue(minFirstLotQty);

    // Calculate Beauty Qty
    const beautyDepthQty = round((beautyFromGrading / 100) * depthQty);
    const maxBeautyQty = beautyDepthQty >= beautyFromClass ? beautyDepthQty : beautyFromClass;
    const comparedMinBeauty = maxBeautyQty <= depthQty ? maxBeautyQty : depthQty;
    const minBeautyQty = facingQty * stackingQty * comparedMinBeauty;

    formItem.get('minBeautyQty').patchValue(minBeautyQty);

    formItem.updateValueAndValidity();
  }

  isNoData(value) {
    return !value && value !== 0;
  }

  validateForm(formItem: AbstractControl, errorMessage: RequestProductErrorEnum) {
    switch (errorMessage) {
      case RequestProductErrorEnum.INVALID_BARCODE:
      case RequestProductErrorEnum.INACTIVE_BARCODE:
        formItem.setValidators(this.setValidator(errorMessage));
        formItem.get('barcode').setValidators(this.setValidator(errorMessage));
        break;
      case RequestProductErrorEnum.STATUS_IS_DELISTED:
      case RequestProductErrorEnum.NOT_ALLOW_FIX_ASSET:
      case RequestProductErrorEnum.NOT_ALLOW_STORE_USE:
        formItem.setValidators(this.setValidator(errorMessage));
        formItem.get('productName').setValidators(this.setValidator(errorMessage));
        break;
      default:
        break;
    }
  }

  setValidator(errorMessage: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      switch (errorMessage) {
        case RequestProductErrorEnum.INVALID_BARCODE:
          return { invalidBarcode: true };
        case RequestProductErrorEnum.INACTIVE_BARCODE:
          return { isInactive: true };
        case RequestProductErrorEnum.STATUS_IS_DELISTED:
          return { isStatusDelisted: true };
        case RequestProductErrorEnum.NOT_ALLOW_FIX_ASSET:
          return { isFixAssetItem: true };
        case RequestProductErrorEnum.NOT_ALLOW_STORE_USE:
          return { isStoreUseItem: true };
        case RequestProductErrorEnum.NOT_ALLOW_ZERO:
          if (control.value !== null) {
            return control.value === 0 ? { isZero: true } : null;
          }

          return null;
        default:
          return null;
      }
    };
  }

  alertErrorModal(message: string) {
    const initialState = {
      title: 'Failed',
      message
    };

    this.modalService.show(AlertModalComponent, {
      initialState
    });
  }

  showDeletedItems() {
    const initialState = {
      title: `${this.deleteMerchandiseItem.length} Item Deleted`,
      data: this.deleteMerchandiseItem,
      columns: [
        { field: 'barcode' },
        { field: 'productName' },
        { field: 'unit', align: 'center' },
        { field: 'unitFactor', align: 'center' }
      ]
    };

    this.modalService.show(SimpleInfoListModalComponent, {
      initialState
    });
  }

  get isViewMode() {
    return this.mode === RequestPageModesEnum.REQUEST_VIEW;
  }

  get isShowDeletedItemsButton() {
    return (
      (this.page === ShelfPages.SHELF_REQUEST || this.page === ShelfPages.SHELF_REQUEST_INVENTORY) &&
      this.type === NewRequestTypeEnum.EDIT &&
      this.mode === RequestPageModesEnum.REQUEST_VIEW
    );
  }

  get isShowChangesMode() {
    return (
      (this.page === ShelfPages.SHELF_REQUEST || this.page === ShelfPages.SHELF_REQUEST_INVENTORY) &&
      this.type === NewRequestTypeEnum.EDIT &&
      (this.mode === RequestPageModesEnum.REQUEST_VIEW || this.status === NewRequestStatusEnum.AWAITING_FIRST_LOT)
    );
  }

  changeStatus(shelfItem: AbstractControl) {
    const barcode = String(shelfItem.get('barcode').value);
    const bayNo = String(shelfItem.get('bayNo').value);
    const bayLevel = String(shelfItem.get('bayLevel').value);
    const locationId = String(shelfItem.get('locationId').value);
    const changeStatus = this.listOfChange.find(item => {
      return (
        item.barcode === barcode &&
        item.bayNo === bayNo &&
        item.bayLevel === bayLevel &&
        item.locationId === locationId
      );
    });

    return changeStatus ? changeStatus.changeStatus : 'NONE';
  }

  hasChanges(shelfItem: AbstractControl, field: string) {
    const barcode = String(shelfItem.get('barcode').value);
    const bayNo = String(shelfItem.get('bayNo').value);
    const bayLevel = String(shelfItem.get('bayLevel').value);
    const locationId = String(shelfItem.get('locationId').value);
    return (
      this.isShowChangesMode &&
      this.listOfChange.find(item => {
        return (
          item.barcode === barcode &&
          item.bayNo === bayNo &&
          item.bayLevel === bayLevel &&
          item.changes.includes(field) &&
          item.locationId === locationId
        );
      })
    );
  }

  get isShowImportShelfButton() {
    return (
      ![NewRequestStatusEnum.AWAITING_FIRST_LOT, NewRequestStatusEnum.ACTIVE].includes(this.status) &&
      this.authGuardService.checkPermission(['shelf_m', 'sh_inv_m'])
    );
  }

  get isShowImportItemButton() {
    return (
      [NewRequestStatusEnum.AWAITING_FIRST_LOT, NewRequestStatusEnum.ACTIVE].includes(this.status) &&
      this.authGuardService.checkPermission(['shelf_firstlot_m'])
    );
  }

  get isShowExportItemButton() {
    return (
      ((this.page === ShelfPages.SHELF_REQUEST ||
        this.page === ShelfPages.SHELF_REQUEST_INVENTORY ||
        this.page === ShelfPages.SHELF_INVENTORY_EDIT) &&
        this.status === NewRequestStatusEnum.AWAITING_FIRST_LOT) ||
      ((this.page === ShelfPages.SHELF_EDIT || this.page === ShelfPages.SHELF_INVENTORY_EDIT) &&
        this.authGuardService.checkPermission(['shelf_firstlot_m', 'shelf_v', 'sh_inv_v']))
    );
  }

  get hasEditPermission() {
    return (
      [RequestPageModesEnum.REQUEST_CREATE, RequestPageModesEnum.REQUEST_EDIT].includes(this.mode) &&
      ![NewRequestStatusEnum.AWAITING_FIRST_LOT, NewRequestStatusEnum.APPROVED, NewRequestStatusEnum.ACTIVE].includes(
        this.status
      ) &&
      this.authGuardService.checkPermission(['shelf_m', 'shelf_firstlot_m', 'sh_inv_m'])
    );
  }

  get requestPageModesEnum() {
    return RequestPageModesEnum;
  }

  saveFile(response: Blob) {
    const blob = new Blob([response]);
    saveAs(blob, `${environment.fileName.exportShelfInventoryTemplate.fileTemplate}.xlsx`);
  }

  downloadErrorExcel() {
    const dateExport = moment().format(environment.fileName.exportShelfInventoryTemplate.timeFormat);
    saveAs(
      this.errorFile.blob,
      `${environment.fileName.exportShelfInventoryTemplate.prefixError} ${dateExport}.xlsx`
    );
  }

  upload(data) {
    this.errorFile = null;
    this.shelfInventoryRequestService.importFileStore(data).subscribe({
      next: (res: any) => {
        this.filesSubmitModal.content.loading = false;
        this.filesSubmitModal.content.isUploadSuccess = true;
        this.dataFilesStoreImport = res;
      },
      error: errorResponse => {
        this.dataFilesStoreImport = null;
        this.filesSubmitModal.content.loading = false;
        let errorMessage = '';
        const hasErrorFile = errorResponse.error && errorResponse.error.fileBase64String;

        if (hasErrorFile) {
          errorMessage = `${errorResponse.error.rowErrorNo} error found. Download this file to edit or delete invalid row then re-import.`;
        } else {
          errorMessage =
            errorResponse.error && errorResponse.error.message ? errorResponse.error.message : errorResponse.message;
        }

        const pattern = /token/i;
        if (errorMessage.match(pattern) && this.filesSubmitModal) {
          this.filesSubmitModal.hide();
          return;
        }

        this.filesSubmitModal.content.setErrorMessage(errorMessage, hasErrorFile);

        if (hasErrorFile) {
          this.errorFile = {
            blob: b64toBlob(errorResponse.error.fileBase64String),
            name: data.name
          };
        }
      }
    });
  }

  onSubmitImport() {
    if (this.dataFilesStoreImport) {
      this.onLoadShelfInventory(this.dataFilesStoreImport);
    }
  }

  onLoadShelfInventory(value: ImportShelfInventoryBarcode) {
    // Reset form
    this.form.clear();

    const form = new UntypedFormArray([], [this.locationIdDuplicateValidator()]);
    value.shelfDetailList.forEach(item => {
      form.push(
        this.createForm(
          this.prepareInventoryItemFromImport(item as InventoryImportItem),
          true,
          item.product.errorMessage
        )
      );
    });

    this.parentForm.setControl('inventoryItem', form);
    this.form.markAsTouched();
    this.filesSubmitModal.hide();
    this.alertSuccessUploadModal();
  }

  alertSuccessUploadModal() {
    this.modalService.show(AlertModalComponent, {
      initialState: {
        title: 'Success',
        message: 'The data have been imported.'
      }
    });
  }

  getBayValues(item: MerchandiseItem): { bayNo: string; bayLevel: string } {
    if (
      [RequestPageModesEnum.REQUEST_VIEW, RequestPageModesEnum.REQUEST_EDIT].includes(this.mode) &&
      item.bayNo &&
      item.bayLevel
    ) {
      return { bayNo: item.bayNo, bayLevel: item.bayLevel };
    } else if (item.bay) {
      return this.splitBay(item.bay);
    }
    return { bayNo: '', bayLevel: '' };
  }

  splitBay(bay: string | undefined | null): { bayNo: string; bayLevel: string } {
    if (!bay || typeof bay !== 'string') {
      return { bayNo: '', bayLevel: '' };
    }
    const [bayNo, bayLevel] = bay.split('/');
    return { bayNo, bayLevel };
  }

  applyFixtureType(formArray: UntypedFormArray, fixtureType: string) {
    formArray.controls.forEach(formGroup => {
      formGroup.get('fixtureType')?.setValue(fixtureType);
    });
  }
}
